import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { getUniqueCurrencies } from "../../utils/currencyOptions";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";
import { formatProductWithSize } from "../utils/productDisplay";
const belongsToShop = (item, shopId) =>
  !shopId || String(item?.shop ?? item?.shop_details?.id ?? "") === String(shopId);

const PurchaseAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleAdd, loading, resetForm } = useAdd("shop/purchases", token);

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop?page_size=all",
    token
  );
  const { data: suppliersData, loading: supplierLoading } = useFetchObjects(
    ["supplier"],
    "shop/suppliers?page_size=all", 
    token
  );
  const { data: productsData, loading: productLoading } = useFetchObjects(
    ["product"],
    "shop/products?page_size=all",
    token
  );
  const { data: unitsData, loading: unitLoading } = useFetchObjects(
    ["unit"],
    "shop/units?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );
  const { data: accountsData, loading: accountLoading } = useFetchObjects(
    ["accounts", "purchase", "add"],
    "shop/accounts/?page_size=all",
    token
  );

  const shops = shopsData?.results || shopsData || [];
  const suppliers = suppliersData?.results || suppliersData || [];
  const products = productsData?.results || productsData || [];
  const units = unitsData?.results || unitsData || [];
  const currencies = currenciesData?.results || currenciesData || [];
  const accounts = accountsData?.results || accountsData || [];
  const hasSingleShop = shops.length === 1;

  const [shop, setShop] = useState("");
  const [supplier, setSupplier] = useState("");
  const [discount, setDiscount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [paymentAccount, setPaymentAccount] = useState("");
  const [paymentExchangeRate, setPaymentExchangeRate] = useState("1");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [purchaseCurrency, setPurchaseCurrency] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!shopLoading && hasSingleShop) {
      setShop(String(shops[0].id));
    }
  }, [shopLoading, hasSingleShop, shops]);

  const branchSuppliers = useMemo(
    () => suppliers.filter((item) => belongsToShop(item, shop)),
    [suppliers, shop]
  );
  const branchProducts = useMemo(
    () => products.filter((item) => belongsToShop(item, shop)),
    [products, shop]
  );
  const branchUnits = useMemo(
    () => units.filter((item) => belongsToShop(item, shop)),
    [units, shop]
  );
  const branchCurrencies = useMemo(
    () => getUniqueCurrencies(currencies.filter((item) => belongsToShop(item, shop))),
    [currencies, shop]
  );
  const branchAccounts = useMemo(
    () => accounts.filter((item) => belongsToShop(item, shop)),
    [accounts, shop]
  );
  const sameCurrencyAccounts = useMemo(
    () =>
      branchAccounts.filter(
        (account) => String(account.currency) === String(purchaseCurrency)
      ),
    [branchAccounts, purchaseCurrency]
  );
  const selectedPaymentAccount = useMemo(
    () =>
      branchAccounts.find(
        (account) => String(account.id) === String(paymentAccount)
      ) || null,
    [branchAccounts, paymentAccount]
  );

  const { data: directRateData } = useFetchObjects(
    ["purchase-add-rate-direct", shop, purchaseCurrency, selectedPaymentAccount?.currency],
    shop && purchaseCurrency && selectedPaymentAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${purchaseCurrency}&to_currency=${selectedPaymentAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["purchase-add-rate-reverse", shop, purchaseCurrency, selectedPaymentAccount?.currency],
    shop && purchaseCurrency && selectedPaymentAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${selectedPaymentAccount.currency}&to_currency=${purchaseCurrency}`
      : "",
    token
  );

  const handleShopChange = (value) => {
    setShop(value);
    setSupplier("");
    setPurchaseCurrency("");
    setReceivedAmount("");
    setPaymentAccount("");
    setPaymentExchangeRate("1");
    setPaymentNotes("");
    setItems([]);
  };

  useEffect(() => {
    if (!purchaseCurrency) {
      setPaymentAccount("");
      setPaymentExchangeRate("1");
      return;
    }
    if (branchAccounts.length === 0) {
      setPaymentAccount("");
      setPaymentExchangeRate("");
      return;
    }

    const exists = branchAccounts.some(
      (account) => String(account.id) === String(paymentAccount)
    );
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || branchAccounts[0];
      setPaymentAccount(preferred ? String(preferred.id) : "");
    }
  }, [purchaseCurrency, paymentAccount, branchAccounts, sameCurrencyAccounts]);

  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        unit: "",
        quantity: "",
        buy_price: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.buy_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const finalTotal = totalAmount - discountValue;

  const itemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product &&
        item.unit &&
        item.quantity &&
        item.buy_price &&
        item.retail_price
    );
  const parsedReceived = parseFloat(receivedAmount) || 0;
  const parsedExchangeRate = parseFloat(paymentExchangeRate) || 0;
  const exchangeRatePreview = useMemo(() => {
    if (!purchaseCurrency) return "1";
    const accountCurrencyId =
      selectedPaymentAccount?.currency ?? purchaseCurrency;
    if (String(purchaseCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [
    purchaseCurrency,
    selectedPaymentAccount,
    directRateData,
    reverseRateData,
  ]);
  const convertedAmount = parsedReceived * parsedExchangeRate;
  const receivedValid = parsedReceived <= finalTotal;

  useEffect(() => {
    setPaymentExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  const isSubmitDisabled = loading;

  const handleSubmit = () => {
    if (!shop) {
      setFormError(t("purchase.shopRequired") || "Branch is required");
      return;
    }
    if (!supplier) {
      setFormError(t("purchase.supplierRequired") || "Supplier is required");
      return;
    }
    if (!purchaseCurrency) {
      setFormError(t("purchase.currencyRequired") || "Currency is required");
      return;
    }
    if (items.length === 0) {
      setFormError(
        t("purchase.atLeastOneItemRequired") ||
          "At least one purchase item is required"
      );
      return;
    }
    if (!itemsValid) {
      setFormError(
        t("purchase.completeItemFields") ||
          "Please complete all required fields for each purchase item"
      );
      return;
    }
    if (!receivedValid) {
      setFormError(
        t("purchase.receivedExceedsTotal") ||
          "Received amount should not exceed total amount"
      );
      return;
    }
    if (parsedReceived > 0 && !paymentAccount) {
      setFormError(t("purchase.accountRequired", "Account is required"));
      return;
    }
    if (parsedReceived > 0 && parsedExchangeRate <= 0) {
      setFormError(
        t("purchase.exchangeRateRequired", "Exchange rate must be greater than 0")
      );
      return;
    }
    setFormError("");
    const payload = {
      shop,
      supplier,
      amount: discountValue,
      received_amount: receivedAmount,
      currency: purchaseCurrency,
      purchase_date: purchaseDate || new Date().toISOString(),
      payment_account: paymentAccount || undefined,
      payment_exchange_rate: paymentExchangeRate || undefined,
      payment_notes: paymentNotes.trim() || undefined,
      items,
    };    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setShop(hasSingleShop ? String(shops[0].id) : "");
      setSupplier("");
      setDiscount("");
      setReceivedAmount("");
      setPaymentAccount("");
      setPaymentExchangeRate("1");
      setPaymentNotes("");
      setPurchaseCurrency("");
      setPurchaseDate("");
      setItems([]);
      setFormError("");
      onClose();
    }
  }, [resetForm, onClose, hasSingleShop, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" dir={fieldDirection}>
      <DialogTitle>{t("purchase.addPurchase")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2, textAlign: fieldTextAlign }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2, direction: fieldDirection }}
        >
          <TextField
            select
            label={t("purchase.selectShop")}
            fullWidth
            required
            value={shop}
            onChange={(e) => handleShopChange(e.target.value)}
            disabled={shopLoading}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {(shops || []).map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("purchase.selectSupplier")}
            fullWidth
            required
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            disabled={supplierLoading || !shop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {(branchSuppliers || []).map((sup) => (
              <MenuItem key={sup.id} value={String(sup.id)}>
                {sup.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("purchase.selectCurrency")}
            fullWidth
            required
            value={purchaseCurrency}
            onChange={(e) => setPurchaseCurrency(e.target.value)}
            disabled={currencyLoading || !shop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {(branchCurrencies || []).map((curr) => (
              <MenuItem key={curr.id} value={String(curr.id)}>
                {curr.title}
              </MenuItem>
            ))}
          </TextField>
          <JalaliDateField
            mode="date"
            label={t("purchase.purchaseDate")}
            fullWidth
            required
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, { InputLabelProps: { shrink: true } })}
          />
          <TextField
            label={t("Discount-Amount")}
            type="number"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <Box sx={{ mt: 2, direction: fieldDirection }}>
            <Typography variant="h6" sx={{ textAlign: fieldTextAlign }}>{t("purchase.purchaseItems")}</Typography>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, direction: fieldDirection }}
              >
                <TextField
                  select
                  label={t("purchase.selectProduct")}
                  required
                  value={item.product}
                  onChange={(e) =>
                    handleItemChange(index, "product", e.target.value)
                  }
                  disabled={productLoading || !shop}
                  SelectProps={selectProps}
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                >
                  {(branchProducts || []).map((prod) => (
                    <MenuItem key={prod.id} value={String(prod.id)}>
                      {formatProductWithSize(prod)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t("purchase.selectUnit")}
                  required
                  value={item.unit}
                  onChange={(e) =>
                    handleItemChange(index, "unit", e.target.value)
                  }
                  disabled={unitLoading || !shop}
                  SelectProps={selectProps}
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                >
                  {(branchUnits || []).map((unit) => (
                    <MenuItem key={unit.id} value={String(unit.id)}>
                      {unit.title}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("purchase.quantity")}
                  type="number"
                  required
                  value={item.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <TextField
                  label={t("purchase.buyPrice")}
                  type="number"
                  required
                  value={item.buy_price}
                  onChange={(e) =>
                    handleItemChange(index, "buy_price", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <TextField
                  label={t("purchase.wholesalePrice", "Wholesale Price")}
                  type="number"
                  value={item.wholesale_price}
                  onChange={(e) =>
                    handleItemChange(index, "wholesale_price", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <TextField
                  label={t("purchase.retailPrice", "Retail Price")}
                  type="number"
                  required
                  value={item.retail_price}
                  onChange={(e) =>
                    handleItemChange(index, "retail_price", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("purchase.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2, textAlign: fieldTextAlign }}>
            <Typography variant="body1">
              {t("purchase.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("purchase.discountAmount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("purchase.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("purchase.receivedAmount", "Received Amount")}
                type="number"
                fullWidth
                value={receivedAmount}
                onChange={(e) => setReceivedAmount(e.target.value)}
                {...getRtlTextFieldProps(formDirection, {
                  inputProps: { min: 0, step: "0.01" },
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label={t("purchase.account", "Account")}
                fullWidth
                value={paymentAccount}
                onChange={(e) => setPaymentAccount(e.target.value)}
                disabled={!shop || accountLoading}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                <MenuItem value="">
                  {t("purchase.selectAccount", "Select Account")}
                </MenuItem>
                {branchAccounts.map((account) => (
                  <MenuItem key={account.id} value={String(account.id)}>
                    {account.name}
                    {account.currency_title ? ` (${account.currency_title})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("purchase.exchangeRate", "Exchange Rate")}
                type="number"
                fullWidth
                value={paymentExchangeRate}
                onChange={(e) => setPaymentExchangeRate(e.target.value)}
                {...getRtlTextFieldProps(formDirection, {
                  inputProps: { min: 0, step: "0.000001" },
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("purchase.convertedAmount", "Converted Amount")}
                fullWidth
                value={convertedAmount.toFixed(2)}
                InputProps={{ readOnly: true }}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("purchase.paymentNotes", "Payment Notes")}
                fullWidth
                multiline
                rows={3}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
          </Grid>
       
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchase.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitDisabled}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchase.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseAdd;
